import { useTablesInput } from 'providers/TablesInputProvider';
import { UseQueryExecute } from 'urql';
import useOfflineQuery from 'hooks/useOfflineQuery';
import {
  DataDisplay,
  ScreenTab,
  TableGroup,
  useTableGroupQuery,
  useTableQuery,
  useTableTabQuery,
} from 'generated/graphql';

/**
 * A tab, group or table of a tables screen: as its parent loaded it, or as its
 * own query did. A lazy tab or group loads on its own once the user opens it,
 * and a table once the user pages it.
 */
export type TableNode<T> = {
  node: T | undefined;
  // Loading on its own with nothing loaded yet, so the node is only as its
  // parent declared it, without its tables.
  isLoading: boolean;
  isFetching: boolean;
  // Refreshes every query the node was loaded by, its parent's first.
  refresh: () => void;
};

const tableNode = <T>(
  declared: T | undefined,
  isOwnLoad: boolean,
  loaded: T | undefined,
  [result, refetch]: [{ fetching: boolean; error?: unknown }, UseQueryExecute],
  refreshParent: () => void,
): TableNode<T> => ({
  // urql keeps the last result while paused, so the declared node wins once
  // the node no longer loads on its own.
  node: (isOwnLoad && loaded) || declared,
  isLoading: isOwnLoad && !loaded && !result.error,
  isFetching: isOwnLoad && result.fetching,
  refresh: () => {
    refreshParent();
    if (isOwnLoad) refetch({ requestPolicy: 'network-only' });
  },
});

export const useTab = (tab: ScreenTab | undefined, refreshScreen: () => void): TableNode<ScreenTab> => {
  const { nodeInput, profileID } = useTablesInput();
  const isOwnLoad = Boolean(tab?.lazyLoad && tab.identifier);
  const query = useOfflineQuery('table-tab-query', useTableTabQuery, {
    variables: { input: nodeInput(tab?.identifier ?? ''), profileID },
    pause: !isOwnLoad,
  });
  return tableNode(tab, isOwnLoad, query[0].data?.tableTab as ScreenTab | undefined, query, refreshScreen);
};

export const useGroup = (group: TableGroup | undefined, refreshTab: () => void): TableNode<TableGroup> => {
  const { nodeInput, profileID } = useTablesInput();
  const isOwnLoad = Boolean(group?.lazyLoad && group.identifier);
  const query = useOfflineQuery('table-group-query', useTableGroupQuery, {
    variables: { input: nodeInput(group?.identifier ?? ''), profileID },
    pause: !isOwnLoad,
  });
  return tableNode(group, isOwnLoad, query[0].data?.tableGroup as TableGroup | undefined, query, refreshTab);
};

// A table pages on identifier, which is empty for a chart, which doesn't page.
export const useTable = (
  table: DataDisplay | undefined,
  identifier: string,
  refreshGroup: () => void,
): TableNode<DataDisplay> => {
  const { nodeInput, pageOf, profileID } = useTablesInput();
  const page = pageOf(identifier);
  const isOwnLoad = Boolean(identifier) && page > 0;
  const query = useOfflineQuery('table-query', useTableQuery, {
    variables: { input: nodeInput(identifier), page, profileID },
    pause: !isOwnLoad,
  });
  return tableNode(
    table,
    isOwnLoad,
    (query[0].data?.table ?? undefined) as DataDisplay | undefined,
    query,
    refreshGroup,
  );
};
